import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  listSessions,
  revokeSession as apiRevokeSession,
  revokeOtherSessions,
  logoutCurrentSession,
} from "../../services/SessionsService";
import Utils from "../../utils";
import useSeo from "../../utils/useSeo";
import PrintTargetSetting from "../../components/PrintTargetSetting";

const deviceIcon = (type) => {
  switch ((type || "").toLowerCase()) {
    case "ios":
    case "android":
      return "bi-phone";
    case "printer":
      return "bi-printer";
    case "api_key":
      return "bi-key";
    case "web":
    default:
      return "bi-display";
  }
};

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return dt.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Security = () => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useSeo({
    title: t("security_and_sessions") || "Segurança e Sessões",
    description:
      "Gerir sessões activas na sua conta TP Software. Terminar sessões em outros dispositivos.",
    path: "/profile/security",
    noindex: true,
  });

  const load = useCallback(() => {
    setLoading(true);
    listSessions()
      .then((r) => setSessions(r?.data?.data || []))
      .catch((err) => toast.error(Utils.getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRevokeOne = async (s) => {
    if (s.is_current) return;
    if (!window.confirm(t("confirm_revoke_session") || "Terminar esta sessão?")) return;
    setBusyId(s.id);
    try {
      await apiRevokeSession(s.id);
      toast.success(t("session_terminated") || "Sessão terminada.");
      load();
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleRevokeOthers = async () => {
    if (!window.confirm(t("confirm_revoke_others") || "Terminar todas as outras sessões?")) return;
    setBusyId("others");
    try {
      const r = await revokeOtherSessions();
      toast.success(r?.data?.message || "OK");
      load();
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const handleLogoutCurrent = async () => {
    if (!window.confirm(t("confirm_logout_current") || "Terminar esta sessão e sair?")) return;
    try {
      await logoutCurrentSession();
      // Axios interceptor no próximo pedido redireccionará para /login
      window.location.replace("/login");
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    }
  };

  return (
    <section className="content" style={{ padding: "30px 24px" }}>
      <div className="container-fluid" style={{ maxWidth: 1100 }}>
        <header style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
            {t("security_and_sessions") || "Segurança e Sessões"}
          </h2>
          <p style={{ color: "#6b7280", margin: "6px 0 0", fontSize: 14 }}>
            {t("security_sessions_hint") ||
              "Aqui vê todos os dispositivos onde a sua conta está autenticada. Termine sessões que não reconheça."}
          </p>
        </header>

        <PrintTargetSetting />

        <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={load}
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise" /> {t("reload") || "Recarregar"}
          </button>
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={handleRevokeOthers}
            disabled={loading || busyId !== null || sessions.filter((s) => !s.is_current).length === 0}
          >
            <i className="bi bi-x-circle" /> {t("revoke_others") || "Terminar todas as outras"}
          </button>
          <button
            type="button"
            className="btn btn-danger ms-auto"
            onClick={handleLogoutCurrent}
          >
            <i className="bi bi-box-arrow-right" /> {t("logout_current") || "Terminar esta sessão"}
          </button>
        </div>

        {loading && sessions.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
            {t("loading") || "A carregar..."}
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#6b7280" }}>
            {t("no_sessions_found") || "Sem sessões activas."}
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {sessions.map((s) => (
              <div
                key={s.id}
                style={{
                  background: s.is_current ? "#f5f3ff" : "#fff",
                  border: `1px solid ${s.is_current ? "#c4b5fd" : "#e5e7eb"}`,
                  borderRadius: 14,
                  padding: "16px 18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                }}
              >
                <div
                  style={{
                    width: 46,
                    height: 46,
                    borderRadius: 12,
                    background: s.is_current ? "#8B5CF6" : "#f3f4f6",
                    color: s.is_current ? "#fff" : "#111827",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                    flex: "0 0 auto",
                  }}
                >
                  <i className={`bi ${deviceIcon(s.device_type)}`} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <strong style={{ fontSize: 15, color: "#111827" }}>
                      {s.device_name ||
                        (s.device_type ? s.device_type.toUpperCase() : "Dispositivo")}
                    </strong>
                    {s.is_current && (
                      <span
                        style={{
                          background: "#8B5CF6",
                          color: "#fff",
                          fontSize: 10,
                          padding: "2px 8px",
                          borderRadius: 999,
                          fontWeight: 700,
                          letterSpacing: 0.5,
                          textTransform: "uppercase",
                        }}
                      >
                        {t("current_session") || "Esta sessão"}
                      </span>
                    )}
                    {s.login_method && s.login_method !== "password" && (
                      <span
                        style={{
                          background: "#dbeafe",
                          color: "#1e40af",
                          fontSize: 10,
                          padding: "2px 8px",
                          borderRadius: 999,
                          fontWeight: 600,
                        }}
                      >
                        {s.login_method}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      color: "#6b7280",
                      fontSize: 12,
                      marginTop: 4,
                      display: "flex",
                      gap: 14,
                      flexWrap: "wrap",
                    }}
                  >
                    {s.platform_os && (
                      <span>
                        <i className="bi bi-laptop" /> {s.platform_os}
                      </span>
                    )}
                    {s.app_version && <span>v{s.app_version}</span>}
                    {(s.city || s.country_code) && (
                      <span>
                        <i className="bi bi-geo-alt" />{" "}
                        {[s.city, s.country_code].filter(Boolean).join(", ")}
                      </span>
                    )}
                    {s.ip_last && (
                      <span>
                        <i className="bi bi-hdd-network" /> {s.ip_last}
                      </span>
                    )}
                  </div>
                  <div style={{ color: "#9ca3af", fontSize: 11, marginTop: 6 }}>
                    <i className="bi bi-clock" /> {t("last_used") || "Última actividade"}:{" "}
                    {formatDate(s.last_used_at)} · {t("created_at") || "Criada em"}:{" "}
                    {formatDate(s.issued_at)}
                  </div>
                </div>
                {!s.is_current && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleRevokeOne(s)}
                    disabled={busyId === s.id}
                    style={{ flex: "0 0 auto" }}
                  >
                    {busyId === s.id
                      ? t("loading")
                      : (
                        <>
                          <i className="bi bi-x-lg" /> {t("terminate") || "Terminar"}
                        </>
                      )}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Security;
